import { useEffect, useState } from 'react';
import useReportError from '../hooks/useReportError';
import Button from './Button';
import {
  GoogleReCaptchaProvider,
  useGoogleReCaptcha
} from 'react-google-recaptcha-v3';
import { CAPTCHA_SITE_KEY } from '../constants/config';

interface Props {
  error: unknown;
}

const stringifiedError = (error: unknown): string => {
  if (Object.prototype.hasOwnProperty.call(error, 'toString')) {
    return (error as { toString: () => string }).toString();
  }

  if (Object.prototype.hasOwnProperty.call(error, 'message')) {
    return (error as Error).message;
  }

  return typeof error;
};

const ErrorReportContent: React.FC<Props> = ({ error }) => {
  const { executeRecaptcha } = useGoogleReCaptcha();
  const [captchaToken, setCaptchaToken] = useState<string>('');
  const errorMessage = stringifiedError(error);
  const [shouldRunReport, setShouldRunReport] = useState(false);

  const { isLoading, isFetched, isSuccess, isError } = useReportError(
    shouldRunReport,
    errorMessage,
    captchaToken
  );

  const reportError = async () => {
    if (CAPTCHA_SITE_KEY && executeRecaptcha) {
      const token = await executeRecaptcha('report_error');
      setCaptchaToken(token);
    }
    setShouldRunReport(true);
  };

  useEffect(() => {
    if (isFetched) {
      setShouldRunReport(false);
    }
  }, [isFetched]);

  return (
    <>
      {!isSuccess && (
        <>
          {!isError && (
            <>
              <h2 className="fz-24 font-yettel mb-4">
                Došlo je do neočekivane greške. Pomozi nam da poboljšamo tvoje
                iskustvo klikom na dugme Prijavi grešku.
              </h2>

              <Button disabled={isLoading} onClick={reportError}>
                Prijavi grešku
              </Button>
            </>
          )}

          {isError && (
            <h2 className="fz-24 font-yettel mb-4">
              Prijava greške nije uspela. Molim te osveži aplikaciju i pokušaj
              kasnije.
            </h2>
          )}
        </>
      )}

      {isSuccess && (
        <h2 className="fz-24 font-yettel mb-4">
          Greška je prijavljena. Hvala ti na pomoći - potrudićemo se da
          otklonimo kvar i poboljšamo tvoje iskustvo!
        </h2>
      )}
    </>
  );
};

const ErrorReport: React.FC<Props> = ({ error }) => {
  if (CAPTCHA_SITE_KEY) {
    return (
      <GoogleReCaptchaProvider reCaptchaKey={CAPTCHA_SITE_KEY} language="sr">
        <ErrorReportContent error={error} />
      </GoogleReCaptchaProvider>
    );
  }

  return <ErrorReportContent error={error} />;
};

export default ErrorReport;
